import { AfterViewInit, ChangeDetectorRef, Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';
import { catchError, forkJoin, map, of } from 'rxjs';
import { LayoutContainerComponent } from '../../components/layout-container/layout-container.component';
import { SectionHeadingComponent } from '../../components/section-heading/section-heading.component';
import { TestimonialCardComponent } from '../../components/testimonial-card/testimonial-card.component';
import { BlogheroComponent } from '../../components/bloghero/bloghero.component';
import { environment } from '../../../environments/environment';
import {
  TestimonialDetail,
  TestimonialIndexItem,
  TestimonialLandingMain
} from '../../core/services/testimonial-cms.models';
import { TestimonialCmsService } from '../../core/services/testimonial-cms.service';

type TestimonialIndexResponse = TestimonialIndexItem[] | string[];

interface TestimonialCardView {
  slug: string;
  imageSrc: string;
  imageAlt: string;
  title: string;
  quote: string;
  name: string;
  role: string;
}

interface TestimonialHeroView {
  slug: string;
  pageTitle: string;
  title: string;
  quote: string;
  text: string;
  author: string;
  role: string;
  date: string;
  imageSrc: string;
  videoSrc: string;
  imageAlt: string;
  posterCaption: string;
  readMoreLabel: string;
  readMoreLink: string;
  contactLabel: string;
  contactLink: string;
  qaLabel: string;
  qaLink: string;
}

@Component({
  selector: 'app-testimonials',
  standalone: true,
  imports: [
    RouterLink,
    BlogheroComponent,
    LayoutContainerComponent,
    SectionHeadingComponent,
    TestimonialCardComponent
  ],
  templateUrl: './testimonials.component.html',
  styleUrl: './testimonials.component.css'
})
export class TestimonialsComponent implements OnInit, AfterViewInit {
  private readonly testimonialCmsService = inject(TestimonialCmsService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly blobPrefix = environment.blobPrefix.replace(/\/+$/, '');
  private indexItems: TestimonialIndexItem[] = [];
  private hydratedHeroSlug: string | null = null;

  private readonly defaultHero: TestimonialHeroView = {
    slug: '',
    pageTitle: 'Customer Feedback',
    title: 'What clients say about working with United Team Building',
    quote: 'What clients say about working with United Team Building',
    text: 'We offer a comprehensive range of services designed to meet the unique needs of your business. From strategy development to risk management, our expert team is dedicated to driving your success.',
    author: '',
    role: '',
    date: '',
    imageSrc: 'assets/customer-feedback-hero.svg',
    videoSrc: '',
    imageAlt: 'Illustration representing client feedback',
    posterCaption: '',
    readMoreLabel: 'Read More',
    readMoreLink: '/testimonial/main',
    contactLabel: 'Contact Us',
    contactLink: '/contact-us',
    qaLabel: 'Q & A',
    qaLink: '/q-and-a'
  };

  private readonly defaultTestimonials: TestimonialCardView[] = [
    {
      slug: 'fun-exciting',
      imageSrc: 'assets/customer-feedback-hero.svg',
      imageAlt: 'Smiling team during a collaborative planning session',
      title: 'Fun and Exciting Team Building',
      quote: 'The experience was well organized, engaging, and easy for our team to participate in from start to finish.',
      name: 'Client Team Lead',
      role: 'Operations'
    },
    {
      slug: 'guaranteed-engage',
      imageSrc: 'assets/customer-feedback-hero.svg',
      imageAlt: 'Colleagues discussing event goals around a table',
      title: 'Guaranteed to Engage Team Members',
      quote: 'United Team Building helped us create a more thoughtful and polished experience for our team event.',
      name: 'HR Coordinator',
      role: 'Human Resources'
    },
    {
      slug: 'configurable',
      imageSrc: 'assets/customer-feedback-hero.svg',
      imageAlt: 'Project manager reviewing event details with confidence',
      title: 'Completely Configurable Experience',
      quote: 'The onboarding and planning communication felt clear, responsive, and professional throughout.',
      name: 'Program Manager',
      role: 'Client Services'
    },
    {
      slug: 'custom-qa',
      imageSrc: 'assets/customer-feedback-hero.svg',
      imageAlt: 'Project manager reviewing event details with confidence',
      title: 'Our Own Questions and Answers',
      quote: 'The onboarding and planning communication felt clear, responsive, and professional throughout.',
      name: 'Program Manager',
      role: 'Client Services'
    },
    {
      slug: 'metrics',
      imageSrc: 'assets/customer-feedback-hero.svg',
      imageAlt: 'Team analyzing performance metrics',
      title: 'Team & Player Metrics',
      quote: 'Individual and Team stats were captured for all questions and answers. A detailed pdf showed the results for the entire event.',
      name: 'Curious George',
      role: 'Head Monkey'
    },
    {
      slug: 'how-it-works',
      imageSrc: 'assets/customer-feedback-hero.svg',
      imageAlt: 'Team participating in a team building activity',
      title: 'How It Works',
      quote: 'United Team Building has put together a unique Team Building Experience that is easy and fun to use. Teams and Players will compete against each other and with each other, creating an engaging team building experience.',
      name: 'Jennifer Froehlich',
      role: 'UTB Support'
    }
  ];

  hero: TestimonialHeroView = this.defaultHero;
  testimonials: TestimonialCardView[] = this.defaultTestimonials;

  ngOnInit(): void {
    this.loadHeroFromCms();
    this.loadTestimonialsFromCms();
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }

    queueMicrotask(() => {
      this.loadHeroFromCms();
      this.loadTestimonialsFromCms();
    });
  }

  private loadHeroFromCms(): void {
    this.testimonialCmsService.getMain()
      .pipe(catchError(() => of(null)))
      .subscribe((main) => {
        this.hero = this.mapHero(main);
        this.tryHydrateHeroFromPrimaryTestimonial();
        this.changeDetectorRef.markForCheck();
      });
  }

  private loadTestimonialsFromCms(): void {
    this.testimonialCmsService.getIndex()
      .pipe(catchError(() => of([])))
      .subscribe((items) => {
        this.indexItems = this.normalizeIndexItems(items);

        const cardSources = this.indexItems.slice(0, Math.max(this.defaultTestimonials.length, 1));

        if (cardSources.length === 0) {
          this.testimonials = this.defaultTestimonials;
          this.tryHydrateHeroFromPrimaryTestimonial();
          this.changeDetectorRef.markForCheck();
          return;
        }

        forkJoin(cardSources.map((item) => this.getCardSource(item)))
          .subscribe((cardItems) => {
            const cmsTestimonials = cardItems.map((item) => this.mapToCard(item));

            this.testimonials = cmsTestimonials.length > 0
              ? cmsTestimonials
              : this.defaultTestimonials;

            this.tryHydrateHeroFromPrimaryTestimonial();
            this.changeDetectorRef.markForCheck();
          });
      });
  }

  private getCardSource(item: TestimonialIndexItem) {
    const slug = this.normalizeText(item.slug);

    if (!slug) {
      return of(item);
    }

    return this.testimonialCmsService.getBySlug(slug)
      .pipe(
        map((detail) => {
          if (!detail) {
            return item;
          }

          return {
            ...item,
            ...detail,
            slug: this.normalizeText(detail.slug) ?? item.slug,
            title: this.normalizeText(detail.title) ?? item.title
          };
        }),
        catchError(() => of(item))
      );
  }

  private tryHydrateHeroFromPrimaryTestimonial(): void {
    if (!this.isHeroUsingPlaceholderMedia()) {
      return;
    }

    const primaryTestimonialSlug = this.indexItems
      .map((item) => this.normalizeText(item.slug))
      .find((slug) => !!slug && slug !== 'main');

    if (!primaryTestimonialSlug) {
      return;
    }

    if (this.hydratedHeroSlug === primaryTestimonialSlug) {
      return;
    }

    this.hydratedHeroSlug = primaryTestimonialSlug;

    this.testimonialCmsService.getBySlug(primaryTestimonialSlug)
      .pipe(catchError(() => of(null)))
      .subscribe((primaryDetail) => {
        if (!primaryDetail) {
          this.hydratedHeroSlug = null;
          return;
        }

        this.applyPrimaryHero(primaryDetail);
      });
  }

  private isHeroUsingPlaceholderMedia(): boolean {
    const heroVideo = this.normalizeText(this.hero.videoSrc);
    if (heroVideo) {
      return false;
    }

    const heroImage = this.normalizeText(this.hero.imageSrc);
    return !heroImage || heroImage === this.defaultHero.imageSrc;
  }

  private applyPrimaryHero(primaryDetail: TestimonialDetail): void {
    const slug = this.normalizeText(primaryDetail.slug);
    const resolvedPosterImage = this.resolveMediaUrl(
      this.normalizeText(primaryDetail.posterImage)
      ?? this.normalizeText(primaryDetail.thumbnailImage)
    );
    const resolvedPosterVideo = this.resolveMediaUrl(this.normalizeText(primaryDetail.posterVideo));
    const { imageSrc: posterImage, videoSrc: posterVideo } = this.normalizePosterMedia(
      resolvedPosterImage,
      resolvedPosterVideo
    );

    this.hero = {
      ...this.hero,
      slug: slug ?? this.hero.slug,
      title: this.normalizeText(primaryDetail.title) ?? this.hero.title,
      quote: this.normalizeText(primaryDetail.quote) ?? this.hero.quote,
      text: this.normalizeText(primaryDetail.textContent)
        ?? this.normalizeText(primaryDetail.summaryContent)
        ?? this.normalizeText(primaryDetail.quote)
        ?? this.hero.text,
      author: this.normalizeText(primaryDetail.author) ?? this.hero.author,
      role: this.normalizeText(primaryDetail.role) ?? this.hero.role,
      date: this.normalizeText(primaryDetail.date) ?? this.hero.date,
      imageSrc: posterImage ?? this.hero.imageSrc,
      videoSrc: posterVideo ?? this.hero.videoSrc,
      posterCaption: this.normalizeText(primaryDetail.posterCaption) ?? this.hero.posterCaption
    };

    this.changeDetectorRef.markForCheck();
  }

  private hasRequiredFields(item: TestimonialIndexItem): boolean {
    return !!this.normalizeText(item?.slug) && !!this.normalizeText(item?.title);
  }

  private normalizeText(value: string | null | undefined): string | null {
    const normalized = value?.trim();
    return normalized ? normalized : null;
  }

  private normalizeIndexItems(items: TestimonialIndexResponse): TestimonialIndexItem[] {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }

    const normalized = items
      .map((item) => {
        if (typeof item === 'string') {
          return this.mapFilenameToIndexItem(item);
        }

        return this.hasRequiredFields(item)
          ? item
          : null;
      })
      .filter((item): item is TestimonialIndexItem => !!item);

    return normalized;
  }

  private mapFilenameToIndexItem(filename: string): TestimonialIndexItem | null {
    const slug = filename.replace(/\.json$/i, '').trim();

    if (!slug || slug === 'index' || slug === 'main') {
      return null;
    }

    const fallback = this.defaultTestimonials.find((item) => item.slug === slug);
    const title = fallback?.title ?? this.humanizeSlug(slug);

    return {
      slug,
      title,
      quote: fallback?.quote,
      author: fallback?.name,
      role: fallback?.role,
      thumbnailImage: fallback?.imageSrc
    };
  }

  private humanizeSlug(slug: string): string {
    return slug
      .split(/[-_]+/)
      .filter((part) => !!part)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private mapToCard(item: TestimonialIndexItem): TestimonialCardView {
    const title = this.normalizeText(item.title) ?? '';

    return {
      slug: this.normalizeText(item.slug) ?? '',
      imageSrc: this.resolveMediaUrl(this.normalizeText(item.thumbnailImage)) ?? '',
      imageAlt: `${title} testimonial image`,
      title,
      quote: this.normalizeText(item.summaryContent)
        ?? this.normalizeText(item.quote)
        ?? '',
      name: this.normalizeText(item.author) ?? '',
      role: this.normalizeText(item.role) ?? ''
    };
  }

  private mapHero(main: TestimonialLandingMain | null): TestimonialHeroView {
    if (!main) {
      return this.defaultHero;
    }

    const slug = this.normalizeText(main.slug) ?? '';
    const resolvedPosterImage = this.resolveMediaUrl(
      this.normalizeText(main.posterImage) ?? this.normalizeText(main.imageSrc)
    );
    const resolvedPosterVideo = this.resolveMediaUrl(this.normalizeText(main.posterVideo));
    const { imageSrc: posterImage, videoSrc: posterVideo } = this.normalizePosterMedia(
      resolvedPosterImage,
      resolvedPosterVideo
    );
    const readMoreLink = slug
      ? `/testimonial/${slug}`
      : this.defaultHero.readMoreLink;

    return {
      slug,
      pageTitle: this.normalizeText(main.pageTitle) ?? this.defaultHero.pageTitle,
      title: this.normalizeText(main.title) ?? this.defaultHero.title,
      quote: this.normalizeText(main.quote)
        ?? this.normalizeText(main.title)
        ?? this.defaultHero.quote,
      text: this.normalizeText(main.textContent)
        ?? this.normalizeText(main.text)
        ?? this.normalizeText(main.summaryContent)
        ?? this.defaultHero.text,
      author: this.normalizeText(main.author) ?? this.defaultHero.author,
      role: this.normalizeText(main.role) ?? this.defaultHero.role,
      date: this.normalizeText(main.date) ?? this.defaultHero.date,
      imageSrc: posterImage ?? this.defaultHero.imageSrc,
      videoSrc: posterVideo ?? this.defaultHero.videoSrc,
      imageAlt: this.normalizeText(main.imageAlt) ?? this.defaultHero.imageAlt,
      posterCaption: this.normalizeText(main.posterCaption) ?? this.defaultHero.posterCaption,
      readMoreLabel: this.normalizeText(main.readMoreLabel) ?? this.defaultHero.readMoreLabel,
      readMoreLink,
      contactLabel: this.normalizeText(main.contactLabel) ?? this.defaultHero.contactLabel,
      contactLink: this.normalizeText(main.contactLink) ?? this.defaultHero.contactLink,
      qaLabel: this.normalizeText(main.qaLabel) ?? this.defaultHero.qaLabel,
      qaLink: this.normalizeText(main.qaLink) ?? this.defaultHero.qaLink
    };
  }

  private resolveMediaUrl(value: string | null | undefined): string | null {
    const normalized = this.normalizeText(value);

    if (!normalized) {
      return null;
    }

    if (/^(https?:)?\/\//i.test(normalized) || normalized.startsWith('data:')) {
      return normalized;
    }

    const relativePath = normalized.replace(/^\/+/, '');
    return `${this.blobPrefix}/${relativePath}`;
  }

  private normalizePosterMedia(
    posterImage: string | null,
    posterVideo: string | null
  ): { imageSrc: string | null; videoSrc: string | null } {
    if (posterVideo && !this.isVideoMediaUrl(posterVideo)) {
      return {
        imageSrc: posterImage ?? posterVideo,
        videoSrc: null
      };
    }

    return {
      imageSrc: posterImage,
      videoSrc: posterVideo
    };
  }

  private isVideoMediaUrl(url: string): boolean {
    return /\.(mp4|webm|ogg|mov|m4v)([?#].*)?$/i.test(url);
  }
}
